/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W6 — FAN CMS-PATH RESOLVER (§33.1, §31).
 *
 * ★ REUSE SEAM (uvcCms.ts §32 / phxCms.ts W4): the universal CMS-doc accessor
 * `getFromCmsData(doc, dot-path, id)` lives in uvcCms.ts. This module IMPORTS it
 * and adds the FAN path-map + label rules (verbatim from fan.selector.js
 * mergeIdsWithCMS:1768-1803 PARAMETER_CMS_PATHS_MAPPING). Nothing is duplicated.
 *
 * The fan selector resolves MOST of its dependent selects through the fans CMS
 * params doc (`fn['current.fan.cms']` = cms/1/v1.0.0/ahu/fans/params/ — the doc
 * is WRAPPED under a top-level `fan` key). The narrowed option list per field is
 * the intersection of the doc's full list with the dependency-graph's
 * parameterPossibleIDs id-array (the `id is Array` branch of getFromCmsData).
 *
 * THREE doc sources (legacy):
 *   - fans params doc (`fan.*` paths)            → most fields
 *   - spring-isolator CMS (`spring_isolator.*`)  → isolator_type/series/mfg/
 *     specified_deflection (CUSTOM_COLLECTIONS, 1804-1811)
 *   - motor-base CMS (`motor_base.*`)            → mtr_base_mfg/series/mtl
 *
 * Fields NOT here (includeAsIs, 1812-1825): mtr_controller_id, *_supplied_by_id,
 * inlet/outlet_guard, protective_cage, fan_afs_*, mounting_method_id,
 * motor_shaft_grounding_id, motor_power — those render from the master param
 * lists loaded by the dependency graph (fanParamUrl), keyed by id with no CMS
 * label rewrite.
 */

import { getFromCmsData, type UvcCmsDoc } from './uvcCms';
import type { CmsNamedRow } from '../cmsDoc';
import type { OptionRow } from '../formKit';

export type FanCmsDoc = UvcCmsDoc;

/** field-name → fans-doc dot-path (mergeIdsWithCMS:1770-1803, verbatim). */
export const FAN_CMS_PATHS: Record<string, string> = {
  mfg_id: 'fan.manufacturer',
  series_id: 'fan.series',
  type_id: 'fan.type',
  arr_id: 'fan.arrangement',
  rotation_id: 'fan.rotation',
  amca_class_id: 'fan.amca_classes',
  wheel_dia_nominal_id: 'fan.wheel.nominal_diameter',
  wheel_dia_percent_id: 'fan.wheel.dia_percent',
  wheel_width_percent_id: 'fan.wheel.width_percent',
  wheel_type_id: 'fan.wheel.type',
  wheel_mtl_id: 'fan.wheel.material',
  style_id: 'fan.style',
  redundancy_id: 'fan.redundancy',
  speed_condition_id: 'fan.speed_condition',
  cube_housing_id: 'fan.cube_housing',
  motor_type_id: 'fan.motor.type',
  fan_inlet_vane_id: 'fan.inlet_vane',
  motor_mfg_id: 'fan.motor.manufacturer',
  motor_series_id: 'fan.motor.series',
  motor_enclosure_id: 'fan.motor.enclosure',
  motor_efficiency_id: 'fan.motor.efficiency',
  motor_housing_id: 'fan.motor.housing',
  motor_speed_nominal_id: 'fan.motor.speed_nominal',
  housed_discharge_id: 'fan.housed_discharge',
  orientation_id: 'fan.orientations',
};

/** motor-position is type-dependent (1800): type 4 → dwdi_arr3, else plenum_arr3. */
export function fanMotorPositionPath(typeId: number | string | null | undefined): string {
  return Number(typeId) == 4
    ? 'fan.motor_position.dwdi_arr3'
    : 'fan.motor_position.plenum_arr3';
}

/** spring-isolator CMS paths (CUSTOM_COLLECTIONS, 1804-1809). */
export const FAN_ISOLATOR_CMS_PATHS: Record<string, string> = {
  isolator_type_id: 'spring_isolator.type',
  isolator_series_id: 'spring_isolator.series',
  isolator_mfg_id: 'spring_isolator.manufacturer',
  specified_deflection: 'spring_isolator.specified_deflection',
  si_mounting_method_id: 'spring_isolator.mounting_method',
};

/** motor-base CMS paths (mergeIdsWithCMS:1796-1798). */
export const FAN_MOTOR_BASE_CMS_PATHS: Record<string, string> = {
  mtr_base_mfg_id: 'motor_base.manufacturer',
  mtr_base_series_id: 'motor_base.series',
  mtr_base_mtl_id: 'motor_base.mtl',
};

/** Resolve a possibly-wrapped doc path (the fans doc nests under `fan`; the
 * form's componentCms may already be unwrapped — strip the leading segment). */
function resolvePath(doc: FanCmsDoc | undefined, path: string): string {
  if (!doc) return path;
  const head = path.split('.')[0];
  if (head in (doc as Record<string, unknown>)) return path;
  return path.split('.').slice(1).join('.');
}

/** The option <text> for a fan CMS row. Most fan selects render `{{value}}`
 * (wheel percents, nominal diameter, speed) OR `{{name}}` OR `{{series}}`. */
export function fanCmsOptionLabel(row: CmsNamedRow | undefined, labelKey?: string): string {
  if (!row) return '';
  if (labelKey && row[labelKey] != null && row[labelKey] !== '') return String(row[labelKey]);
  if (row.name != null && row.name !== '') return String(row.name);
  if (row.value != null && row.value !== '') return String(row.value);
  if (row.series != null && row.series !== '') return String(row.series);
  return '';
}

/** Is this field resolved via the fans CMS doc? */
export function isFanCmsField(field: string): boolean {
  return field in FAN_CMS_PATHS;
}

/** OPTION LIST for a doc-resolved fan select. ids==null ⇒ FULL list. */
export function fanCmsOptionList(
  doc: FanCmsDoc | undefined,
  field: string,
  ids?: Array<number | string> | null,
  labelKey?: string
): OptionRow[] {
  const path = field === 'motor_position_id' ? undefined : FAN_CMS_PATHS[field];
  if (!path) return [];
  const result = getFromCmsData(doc, resolvePath(doc, path), ids ?? null);
  const arr = Array.isArray(result) ? result : result ? [result] : [];
  return arr.map((row) => ({
    ...row,
    id: row.id,
    label: fanCmsOptionLabel(row, labelKey),
  })) as OptionRow[];
}

/** OPTION LIST for an arbitrary dot-path against a given doc (isolator / motor
 * base / motor_position). */
export function fanCmsOptionListByPath(
  doc: FanCmsDoc | undefined,
  path: string,
  ids?: Array<number | string> | null,
  labelKey?: string
): OptionRow[] {
  if (!path) return [];
  const result = getFromCmsData(doc, resolvePath(doc, path), ids ?? null);
  const arr = Array.isArray(result) ? result : result ? [result] : [];
  return arr.map((row) => ({
    ...row,
    id: row.id,
    label: fanCmsOptionLabel(row, labelKey),
  })) as OptionRow[];
}

/** DISPLAY LABEL for the currently-selected id of a doc-resolved fan select. */
export function fanCmsSelectedLabel(
  doc: FanCmsDoc | undefined,
  field: string,
  id: number | string | null | undefined,
  labelKey?: string
): string {
  if (id == null || id === '') return '';
  const path = FAN_CMS_PATHS[field];
  if (!path) return '';
  const row = getFromCmsData(doc, resolvePath(doc, path), id);
  return fanCmsOptionLabel(Array.isArray(row) ? row[0] : row, labelKey);
}

/** DISPLAY LABEL via an explicit path (isolator/motor-base/motor-position). */
export function fanCmsSelectedLabelByPath(
  doc: FanCmsDoc | undefined,
  path: string,
  id: number | string | null | undefined,
  labelKey?: string
): string {
  if (id == null || id === '' || !path) return '';
  const row = getFromCmsData(doc, resolvePath(doc, path), id);
  return fanCmsOptionLabel(Array.isArray(row) ? row[0] : row, labelKey);
}

/** The fan `type` value (e.g. 'plenum'/'dwdi'/'plug'/'cube') for the chosen
 * type_id — the envelope `type` field + per-form layout switches. */
export function fanTypeValue(
  doc: FanCmsDoc | undefined,
  typeId: number | string | null | undefined
): string | undefined {
  if (typeId == null) return undefined;
  const row = getFromCmsData(doc, resolvePath(doc, 'fan.type'), typeId);
  const r = Array.isArray(row) ? row[0] : row;
  return r ? String((r as CmsNamedRow).value ?? '') : undefined;
}

/** The arrangement `value` (e.g. '3'/'4') for the chosen arr_id — the other
 * half of the `<type>_arr<arr>` mfg-settings URL legacy builds in updateQty
 * (fan.selector.js:3789-3806, model/fan.cms.setting.js). */
export function fanArrValue(
  doc: FanCmsDoc | undefined,
  arrId: number | string | null | undefined
): string | undefined {
  if (arrId == null) return undefined;
  const row = getFromCmsData(doc, resolvePath(doc, 'fan.arrangement'), arrId);
  const r = Array.isArray(row) ? row[0] : row;
  return r ? String((r as CmsNamedRow).value ?? '') : undefined;
}
